import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api/v1`;

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

let accessTokenRef = null;
let onAuthFailure = null;

export const setAccessToken = (token) => {
    accessTokenRef = token;
};

export const getAccessToken = () => accessTokenRef;

export const setOnAuthFailure = (handler) => {
    onAuthFailure = handler;
};

api.interceptors.request.use((config) => {
    if (accessTokenRef) {
        config.headers.Authorization = `Bearer ${accessTokenRef}`;
    }
    return config;
});

let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
    refreshQueue.forEach(({ resolve, reject }) => {
        if (error) reject(error);
        else resolve(token);
    });
    refreshQueue = [];
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (!originalRequest) return Promise.reject(error);

        // Don't try to refresh on the refresh endpoint itself or auth endpoints
        const isAuthEndpoint =
            originalRequest.url?.includes("/auth/login") ||
            originalRequest.url?.includes("/auth/refresh");

        if (
            error.response?.status === 401 &&
            !originalRequest._retry &&
            !isAuthEndpoint
        ) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    refreshQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return api(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }
            originalRequest._retry = true;
            isRefreshing = true;
            try {
                const resp = await api.post("/auth/refresh");
                const newToken = resp.data?.data?.access_token;
                if (newToken) {
                    accessTokenRef = newToken;
                    processQueue(null, newToken);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return api(originalRequest);
                }
                throw new Error("Token refresh failed");
            } catch (e) {
                processQueue(e, null);
                accessTokenRef = null;
                if (onAuthFailure) onAuthFailure();
                return Promise.reject(e);
            } finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error);
    },
);

// Helpers — unwrap envelope automatically
export const apiGet = async (url, config) => {
    const r = await api.get(url, config);
    return r.data?.data;
};
export const apiPost = async (url, body, config) => {
    const r = await api.post(url, body, config);
    return r.data?.data;
};
export const apiPatch = async (url, body, config) => {
    const r = await api.patch(url, body, config);
    return r.data?.data;
};
export const apiDelete = async (url, config) => {
    const r = await api.delete(url, config);
    return r.data?.data;
};

export const apiGetWithMeta = async (url, config) => {
    const r = await api.get(url, config);
    return { data: r.data?.data, meta: r.data?.meta };
};

export const apiPostFormData = async (url, formData, config = {}) => {
    // Let the browser set the multipart boundary automatically — explicit
    // Content-Type breaks the boundary header.
    const r = await api.post(url, formData, {
        ...config,
        headers: { ...(config.headers || {}), "Content-Type": "multipart/form-data" },
    });
    return r.data?.data;
};
